// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'
import { ProtectedRoute } from '@/lib/auth/ProtectedRoute'
import { AppShell } from '@/components/layout/AppShell'
import { Spinner } from '@/components/ui/Spinner'

// Auth pages — eager loaded (small)
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { MicrosoftAuthCompletePage } from '@/pages/auth/MicrosoftAuthCompletePage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage'
import { NotFoundPage } from '@/pages/errors/NotFoundPage'

// App pages — lazy loaded for code splitting
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })))
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })))
const ApiKeysPage = lazy(() => import('@/pages/api-keys/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })))
const BillingPage = lazy(() => import('@/pages/billing/BillingPage').then((m) => ({ default: m.BillingPage })))
const WebhooksPage = lazy(() => import('@/pages/webhooks/WebhooksPage').then((m) => ({ default: m.WebhooksPage })))
const SchemaRegistryPage = lazy(() => import('@/pages/schema-registry/SchemaRegistryPage').then((m) => ({ default: m.SchemaRegistryPage })))
const AuditLogPage = lazy(() => import('@/pages/audit-log/AuditLogPage').then((m) => ({ default: m.AuditLogPage })))
const NotificationsInboxPage = lazy(() =>
  import('@/pages/notifications/NotificationsInboxPage').then((m) => ({ default: m.NotificationsInboxPage })),
)
const SettingsLayout = lazy(() => import('@/pages/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })))
const ProfilePage = lazy(() => import('@/pages/settings/ProfilePage').then((m) => ({ default: m.ProfilePage })))
const OrganizationPage = lazy(() => import('@/pages/settings/OrganizationPage').then((m) => ({ default: m.OrganizationPage })))
const TeamPage = lazy(() => import('@/pages/settings/TeamPage').then((m) => ({ default: m.TeamPage })))
const SecurityPage = lazy(() => import('@/pages/settings/SecurityPage').then((m) => ({ default: m.SecurityPage })))
const NotificationsPage = lazy(() => import('@/pages/settings/NotificationsPage').then((m) => ({ default: m.NotificationsPage })))
const OrgCommunicationsPage = lazy(() =>
  import('@/pages/settings/OrgCommunicationsPage').then((m) => ({ default: m.OrgCommunicationsPage })),
)

function PageFallback() {
  return (
    <div className="flex h-full items-center justify-center py-16">
      <Spinner />
    </div>
  )
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route path="/signup" element={<Navigate to={ROUTES.REGISTER} replace />} />
      <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
      <Route path={ROUTES.MICROSOFT_AUTH_COMPLETE} element={<MicrosoftAuthCompletePage />} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route path={ROUTES.ACCEPT_INVITE} element={<AcceptInvitePage />} />

      {/* Protected routes — wrapped in AppShell */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route
          path={ROUTES.DASHBOARD}
          element={<Suspense fallback={<PageFallback />}><DashboardPage /></Suspense>}
        />
        <Route
          path={ROUTES.DOCUMENTS}
          element={<Suspense fallback={<PageFallback />}><DocumentsPage /></Suspense>}
        />
        <Route
          path={ROUTES.API_KEYS}
          element={<Suspense fallback={<PageFallback />}><ApiKeysPage /></Suspense>}
        />
        <Route
          path={ROUTES.BILLING}
          element={<Suspense fallback={<PageFallback />}><BillingPage /></Suspense>}
        />
        <Route
          path={ROUTES.WEBHOOKS}
          element={<Suspense fallback={<PageFallback />}><WebhooksPage /></Suspense>}
        />
        <Route
          path={ROUTES.SCHEMA_REGISTRY}
          element={<Suspense fallback={<PageFallback />}><SchemaRegistryPage /></Suspense>}
        />
        <Route
          path={ROUTES.AUDIT_LOG}
          element={<Suspense fallback={<PageFallback />}><AuditLogPage /></Suspense>}
        />
        <Route
          path={ROUTES.NOTIFICATIONS}
          element={<Suspense fallback={<PageFallback />}><NotificationsInboxPage /></Suspense>}
        />
        <Route
          element={<Suspense fallback={<PageFallback />}><SettingsLayout /></Suspense>}
        >
          <Route path={ROUTES.SETTINGS} element={<Navigate to={ROUTES.SETTINGS_PROFILE} replace />} />
          <Route path={ROUTES.SETTINGS_PROFILE} element={<Suspense fallback={<PageFallback />}><ProfilePage /></Suspense>} />
          <Route path={ROUTES.SETTINGS_ORGANIZATION} element={<Suspense fallback={<PageFallback />}><OrganizationPage /></Suspense>} />
          <Route path={ROUTES.SETTINGS_TEAM} element={<Suspense fallback={<PageFallback />}><TeamPage /></Suspense>} />
          <Route path={ROUTES.SETTINGS_SECURITY} element={<Suspense fallback={<PageFallback />}><SecurityPage /></Suspense>} />
          <Route path={ROUTES.SETTINGS_NOTIFICATIONS} element={<Suspense fallback={<PageFallback />}><NotificationsPage /></Suspense>} />
          <Route path={ROUTES.SETTINGS_COMMUNICATIONS} element={<Suspense fallback={<PageFallback />}><OrgCommunicationsPage /></Suspense>} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
    </Routes>
  )
}
